import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Table, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import '../AdminDashboard.css';
import './PricingPlanManagement.css';
import MasterFeatureListManagement from './MasterFeatureListManagement';

// Cognitive Agents plans price themselves from these five inputs; every other
// category keeps using the plain `price` field.
const AGENT_PLAN_CATEGORY = 'cognitive_agents';
const EMPTY_AGENT_FEES = {
  platformFee: '',        // rupees in the form; stored as paise
  includedRuns: '',
  includedCredits: '',
  grossMarginPct: '50',   // percent in the form; stored as a 0-1 fraction
  annualDiscountPct: '',
  // Presentation only — never charged from. See agentPlan.js.
  originalPrice: '',
  isPopular: false
};

/** What the customer is actually charged per period — mirrors agentPlan.js
 *  server-side, shown live so the admin is never guessing. */
const agentPlanCharge = (fees, interval) => {
  const monthly = Number(fees?.platformFee) || 0;
  if (interval !== 'yearly') return monthly;
  return Math.floor(monthly * 12 * (1 - (Number(fees?.annualDiscountPct) || 0) / 100));
};


/**
 * Run calculator — turns a Platform Fee into an Included Runs figure.
 *
 * The plan's fee is the input an admin actually has an opinion about; how many
 * runs it can pay for is arithmetic, and doing that arithmetic by eye is how a
 * plan ships that is underwater on every customer who uses it. Enter the fee,
 * the runs field fills itself.
 *
 * Per-run PRICE comes from POST /plans/estimate-runs, which runs the production
 * pricer (billingService.calculateUsageCharge) at this plan's own gross margin.
 * A browser-side copy of that formula would be a second source of truth for the
 * amount the customer is eventually charged, and the two would diverge the
 * first time a vendor rate moved. See docs/COGNITIVE_FLOWS_BILLING_FORMULA.md.
 *
 * What IS done here is division — fee ÷ price-per-run, credits ÷ credits-per-run.
 * That is plan arithmetic, not billing, and keeping it local is what lets the
 * runs field update on the keystroke instead of after a round trip. The fetch
 * therefore depends only on the two inputs that move the PRICE (margin and
 * sandbox tier), which also means auto-filling the runs field can never feed
 * back into its own request.
 */
const PlanRunCalculator = ({ appUrl, fees, currency, autoApply, onApplyRuns, onAutoChange }) => {
  const [tier, setTier] = useState('1g');
  const [sizingProfile, setSizingProfile] = useState('typical');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const platformFeeMinorUnits = Math.round((Number(fees?.platformFee) || 0) * 100);
  const includedCredits = Number(fees?.includedCredits) || 0;
  const includedRuns = Number(fees?.includedRuns) || 0;
  const grossMarginTarget = (Number(fees?.grossMarginPct) || 0) / 100;

  useEffect(() => {
    // Debounced — the margin field is typed a digit at a time.
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await axios.post(`${appUrl}/api/subscriptions/plans/estimate-runs`, {
          grossMarginTarget,
          sandboxMemoryTier: tier,
        }, { withCredentials: true });
        if (cancelled) return;
        if (resp.data.success) setData(resp.data.data);
        else setError(resp.data.message || 'Could not price this plan');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || 'Could not reach the pricing service');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [appUrl, grossMarginTarget, tier]);

  const estimates = data?.estimates || [];
  const sized = estimates.find((e) => e.key === sizingProfile) || null;
  const pricePerRun = Number(sized?.pricePerRunMinorUnits) || 0;

  // Break-even bundle: the fee already includes the plan's margin, so this is
  // the largest number of runs the fee pays for without eating into it.
  const derivedRuns = pricePerRun > 0
    ? Math.floor(platformFeeMinorUnits / pricePerRun)
    : null;

  // Fill the runs field as the fee is typed. Guarded on the value actually
  // differing so this settles after one pass instead of re-rendering forever,
  // and gated on `autoApply` so opening an existing plan to edit its
  // description never silently rewrites what its subscribers were sold.
  useEffect(() => {
    if (!autoApply || derivedRuns == null) return;
    if (String(derivedRuns) === String(fees?.includedRuns ?? '')) return;
    onApplyRuns(derivedRuns);
  }, [autoApply, derivedRuns, fees?.includedRuns, onApplyRuns]);

  const money = (minorUnits) => `₹${((Number(minorUnits) || 0) / 100).toFixed(2)}`;
  const runsFor = (estimate) => {
    const price = Number(estimate.pricePerRunMinorUnits) || 0;
    return price > 0 ? Math.floor(platformFeeMinorUnits / price) : null;
  };
  const creditRunsFor = (estimate) => {
    const perRun = Number(estimate.creditsPerRun) || 0;
    return perRun > 0 ? Math.floor(includedCredits / perRun) : null;
  };

  // The plan PROMISES `includedRuns`. Whether the fee earns them and whether
  // the credit allowance can pay for them are two separate questions, and both
  // can be answered wrong in a way nobody notices until a customer is blocked
  // or a month's margin is gone.
  const verdictFor = (estimate) => {
    if (!includedRuns) return null;
    const creditsNeeded = (Number(estimate.creditsPerRun) || 0) * includedRuns;
    const feeRuns = runsFor(estimate);

    if (feeRuns != null && includedRuns > feeRuns) {
      return {
        tone: 'text-danger',
        icon: '⚠',
        text: `Fee covers ${feeRuns.toLocaleString()} runs — this plan promises ${includedRuns.toLocaleString()}, so it loses money past run ${(feeRuns + 1).toLocaleString()}`,
      };
    }
    if (includedCredits > 0 && creditsNeeded > includedCredits) {
      return {
        tone: 'text-warning',
        icon: '⚠',
        text: `Credits run out after ${(creditRunsFor(estimate) ?? 0).toLocaleString()} of ${includedRuns.toLocaleString()} runs — needs ${creditsNeeded.toLocaleString()}`,
      };
    }
    return {
      tone: 'text-success',
      icon: '✓',
      text: `Fee and credits both cover all ${includedRuns.toLocaleString()} runs`,
    };
  };

  return (
    <div className="mb-3 p-3 border rounded" style={{ background: '#fff' }}>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div style={{ flex: '1 1 260px' }}>
          <div className="fw-semibold">Run calculator</div>
          <Form.Text className="text-muted d-block">
            Prices one run with the live billing formula at this plan&apos;s gross margin, then
            divides the Platform Fee by it to fill Included Runs. Runs debit whole credits
            today, so a &#8377;3.72 run costs 4.
          </Form.Text>
        </div>
        <Form.Group style={{ minWidth: 190 }}>
          <Form.Label className="mb-1 small">Size runs from</Form.Label>
          <Form.Select size="sm" value={sizingProfile} onChange={(e) => setSizingProfile(e.target.value)}>
            {estimates.map((estimate) => (
              <option key={estimate.key} value={estimate.key}>{estimate.label}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group style={{ minWidth: 175 }}>
          <Form.Label className="mb-1 small">Sandbox memory</Form.Label>
          <Form.Select size="sm" value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="1g">1 GB (production)</option>
            <option value="4g">4 GB (code default)</option>
            <option value="16g">16 GB</option>
            <option value="64g">64 GB</option>
          </Form.Select>
        </Form.Group>
      </div>

      <Form.Check
        type="checkbox"
        id="agent-plan-auto-runs"
        className="mb-2"
        label="Auto-fill Included Runs from the Platform Fee"
        checked={!!autoApply}
        onChange={(e) => onAutoChange(e.target.checked)}
      />

      {currency && currency !== 'INR' && (
        <Alert variant="warning" className="py-2 px-3 small mb-2">
          Usage credits are denominated in rupees (1 credit = &#8377;1) regardless of the
          plan&apos;s billing currency, so the figures below stay in &#8377;.
        </Alert>
      )}

      {error && <Alert variant="danger" className="py-2 px-3 small mb-2">{error}</Alert>}

      {!data && loading && (
        <div className="d-flex align-items-center gap-2 text-muted small">
          <Spinner animation="border" size="sm" /> Pricing&hellip;
        </div>
      )}

      {data && (
        <>
          {platformFeeMinorUnits === 0 && (
            <Alert variant="secondary" className="py-2 px-3 small mb-2">
              Enter a Platform Fee above and Included Runs will fill in from it.
            </Alert>
          )}

          {/* A fee too small to buy one run auto-fills 0, and the server refuses
              to save a plan with 0 included runs — it would block every run its
              subscriber starts. Say why here rather than letting Save fail with
              a validation message that looks unrelated to the fee. */}
          {platformFeeMinorUnits > 0 && derivedRuns === 0 && (
            <Alert variant="danger" className="py-2 px-3 small mb-2">
              A {money(platformFeeMinorUnits)} fee does not cover a single{' '}
              <strong>{sized?.label || sizingProfile}</strong> run at {money(pricePerRun)}.
              Raise the fee, size from a lighter profile, or set Included Runs by hand —
              a plan saved with 0 runs blocks every run its subscribers start.
            </Alert>
          )}

          <Table size="sm" responsive className="mb-2 align-middle" style={{ opacity: loading ? 0.55 : 1 }}>
            <thead>
              <tr>
                <th>Run profile</th>
                <th className="text-end">Cost / run</th>
                <th className="text-end">Credits / run</th>
                <th className="text-end">Runs the fee covers</th>
                <th className="text-end">Runs the credits cover</th>
              </tr>
            </thead>
            <tbody>
              {estimates.map((estimate) => {
                const verdict = verdictFor(estimate);
                const feeRuns = runsFor(estimate);
                const isSizing = estimate.key === sizingProfile;
                return (
                  <tr key={estimate.key} className={isSizing ? 'table-active' : undefined}>
                    <td>
                      <div className="fw-semibold">
                        {estimate.label}
                        {isSizing && <Badge bg="secondary" className="ms-2 fw-normal">sizing</Badge>}
                      </div>
                      <div className="text-muted small">{estimate.description}</div>
                      {verdict && (
                        <div className={`small ${verdict.tone}`}>{verdict.icon} {verdict.text}</div>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="fw-semibold">{money(estimate.pricePerRunMinorUnits)}</div>
                      <div className="text-muted small">
                        {money(estimate.passThroughCostMinorUnits)} cost + {money(estimate.runtimeFeeMinorUnits)} runtime
                      </div>
                    </td>
                    <td className="text-end fw-semibold">{(estimate.creditsPerRun || 0).toLocaleString()}</td>
                    <td className="text-end">
                      <div className="fw-semibold">{feeRuns == null ? '—' : feeRuns.toLocaleString()}</div>
                      {!isSizing && feeRuns != null && (
                        <Button
                          variant="link" size="sm" className="p-0 small"
                          onClick={() => setSizingProfile(estimate.key)}
                        >
                          Size from this
                        </Button>
                      )}
                    </td>
                    <td className="text-end">
                      {includedCredits > 0 ? (creditRunsFor(estimate) ?? 0).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Form.Text className="text-muted">
            Margin {((estimates[0]?.grossMarginTarget || 0) * 100).toFixed(0)}% on vendor cost;
            the runtime fee is charged at cost and earns none. Pricing version{' '}
            {estimates[0]?.pricingVersion}.
          </Form.Text>
        </>
      )}
    </div>
  );
};

const PricingPlanManagement = ({ ViewedPage, app_url, embedded = false }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'INR',
    interval: 'monthly',
    features: [],
    razorpay_plan_id: '',
    is_active: true,
    visible_to_shops: [],
    // Cognitive Agents plans only. Kept nested so a landing-page plan is never
    // half-described by fields it does not use.
    fees: EMPTY_AGENT_FEES
  });
  const [newFeature, setNewFeature] = useState('');
  const [saving, setSaving] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [selectedPlanForVisibility, setSelectedPlanForVisibility] = useState(null);
  const [visibilityShopSearch, setVisibilityShopSearch] = useState('');
  const [availablePages, setAvailablePages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [featureSearch, setFeatureSearch] = useState('');
  const [masterFeatures, setMasterFeatures] = useState([]);
  // Which plan a new signup is provisioned onto right now, as resolved by the
  // server — not necessarily the one anybody ticked.
  const [defaultPlanId, setDefaultPlanId] = useState(null);
  const [masterFeaturesLoading, setMasterFeaturesLoading] = useState(false);
  const [showMasterFeaturesModal, setShowMasterFeaturesModal] = useState(false);
  // Included Runs is derived from the Platform Fee by the run calculator, but
  // only once the fee has actually been TYPED. Opening an existing plan must
  // not silently rewrite the allowance its subscribers were sold, so the
  // trigger is the edit, not the modal.
  const [autoRunsFromFee, setAutoRunsFromFee] = useState(true);
  const [platformFeeTouched, setPlatformFeeTouched] = useState(false);
  // Stable identity: the calculator lists this in an effect dependency array,
  // and a fresh closure each render would re-run that effect every render.
  const applyDerivedRuns = useCallback((runs) => {
    setFormData((prev) => ({ ...prev, fees: { ...prev.fees, includedRuns: String(runs) } }));
  }, []);
  const user_role = Cookies.get('user_role');

  // Plan category → default page names
  const KEYWORD_PERSONALIZATION_PAGES = [
    'Google & Meta Integration',
    'Keyword Intent Optimization',
    'Groups',
    'Meta Ads Personalization',
    'All Campaign',
    'Create Campaign',
    'AI Mode LP Optimization',
    'Quality Score Graph',
    'Opportunities',
    'Domains & URLs',
    'Business Context',
  ];
  const LAYOUT_PERSONALIZATION_PAGES = [
    ...KEYWORD_PERSONALIZATION_PAGES,
    'Existing Divs',
    'Saved Section',
    'Assets Section',
    'AI Assets Gen',
    'Analytics Dashboard',
    'Tags',
  ];

  // Infer plan category from features when planCategory field is missing (legacy plans)
  const LP_ONLY_PAGES = ['Existing Divs', 'Saved Section', 'Assets Section', 'AI Assets Gen', 'Analytics Dashboard', 'Tags'];
  const inferPlanCategory = (features) => {
    if (!Array.isArray(features) || features.length === 0) return '';
    if (LP_ONLY_PAGES.some(p => features.includes(p))) return 'layout_personalization';
    if (KEYWORD_PERSONALIZATION_PAGES.some(p => features.includes(p))) return 'keyword_personalization';
    return '';
  };

  // A master-feature id can arrive as a plain string, an ObjectId, or the
  // `{ $oid }` shape depending on which write path stored it. Comparing with
  // `===` silently drops the bullets on any plan not written by this form.
  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.$oid) return value.$oid;
    return typeof value?.toString === 'function' ? value.toString() : null;
  };

  // Filter shops for visibility list by search (shop key or name)
  const getFilteredShopsForVisibility = (shopList) => {
    const q = (visibilityShopSearch || '').trim().toLowerCase();
    if (!q) return shopList;
    return shopList.filter((s) => {
      const name = (s.shop_name || s.shop || '').toLowerCase();
      const key = (s.shop || '').toLowerCase();
      return name.includes(q) || key.includes(q);
    });
  };

  useEffect(() => {
    ViewedPage(105);
    fetchPlans();
    fetchShops();
    fetchAvailablePages();
    fetchMasterFeatures();
  }, [ViewedPage]);

  const fetchAvailablePages = async () => {
    setPagesLoading(true);
    try {
      const resp = await axios.get(`${app_url}/api/admin/available-pages`, { withCredentials: true });
      const mapping = resp.data?.data?.all_pages_mapping || resp.data?.all_pages_mapping || {};
      setAvailablePages(Object.keys(mapping));
    } catch (e) {
      console.error('Failed to load available pages', e);
    } finally {
      setPagesLoading(false);
    }
  };

  const fetchMasterFeatures = async () => {
    setMasterFeaturesLoading(true);
    try {
      const resp = await axios.get(`${app_url}/api/subscriptions/master-features`, { withCredentials: true });
      if (resp.data.success) {
        setMasterFeatures(resp.data.data || []);
      }
    } catch (e) {
      console.error('Failed to load master features', e);
    } finally {
      setMasterFeaturesLoading(false);
    }
  };

  const fetchShops = async () => {
    setShopsLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/shops`, {
        withCredentials: true
      });
      if (response.data.success) {
        setShops(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setShopsLoading(false);
    }
  };

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/plans?include_inactive=true`, {
        withCredentials: true
      });
      if (response.data.success) {
        setPlans(response.data.data || []);
        setDefaultPlanId(response.data.defaultPlanId || null);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'INR',
      interval: 'monthly',
      features: [],
      planCategory: '',
      fees: EMPTY_AGENT_FEES,
      razorpay_plan_id: '',
      is_active: true,
      visible_to_shops: [],
      masterFeatures: [],
      isDefaultPlan: false
    });
    setAutoRunsFromFee(true);
    setPlatformFeeTouched(false);
    setVisibilityShopSearch('');
    setFeatureSearch('');
    setShowCreateModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      price: plan.price.toString(),
      currency: plan.currency,
      interval: plan.interval,
      features: plan.features || [],
      planCategory: plan.planCategory || inferPlanCategory(plan.features),
      fees: plan.fees ? {
        platformFee: String((plan.fees.platformFeeMinorUnits ?? 0) / 100),
        includedRuns: String(plan.fees.includedRuns ?? ''),
        includedCredits: String(plan.fees.includedCredits ?? ''),
        grossMarginPct: String(Math.round((plan.fees.grossMarginTarget ?? 0.5) * 100)),
        annualDiscountPct: String(Math.round((plan.fees.annualDiscountPct ?? 0) * 100)),
        originalPrice: plan.fees.originalPriceMinorUnits
          ? String(plan.fees.originalPriceMinorUnits / 100)
          : '',
        isPopular: plan.fees.isPopular === true
      } : EMPTY_AGENT_FEES,
      razorpay_plan_id: plan.razorpay_plan_id || '',
      is_active: plan.is_active !== undefined ? plan.is_active : true,
      visible_to_shops: plan.visible_to_shops || [],
      masterFeatures: plan.masterFeatures || [],
      isDefaultPlan: plan.isDefaultPlan === true
    });
    setAutoRunsFromFee(true);
    setPlatformFeeTouched(false);
    setVisibilityShopSearch('');
    setFeatureSearch('');
    setShowCreateModal(true);
  };

  const handleManageVisibility = (plan) => {
    setSelectedPlanForVisibility(plan);
    setFormData(prev => ({
      ...prev,
      visible_to_shops: plan.visible_to_shops || []
    }));
    setVisibilityShopSearch('');
    setShowVisibilityModal(true);
  };

  const handleSaveVisibility = async () => {
    if (!selectedPlanForVisibility) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${app_url}/api/subscriptions/plans/${selectedPlanForVisibility._id}/visibility`,
        {
          visible_to_shops: formData.visible_to_shops
        },
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast.success('Plan visibility updated successfully');
        setShowVisibilityModal(false);
        fetchPlans();
      } else {
        toast.error(response.data.message || 'Failed to update plan visibility');
      }
    } catch (error) {
      console.error('Error updating plan visibility:', error);
      toast.error(error.response?.data?.message || 'Failed to update plan visibility');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShopVisibility = (shop) => {
    const shopKey = shop.shop || shop;
    setFormData(prev => {
      const currentShops = prev.visible_to_shops || [];
      if (currentShops.includes(shopKey)) {
        return {
          ...prev,
          visible_to_shops: currentShops.filter(s => s !== shopKey)
        };
      } else {
        return {
          ...prev,
          visible_to_shops: [...currentShops, shopKey]
        };
      }
    });
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  // A save can succeed and still not do what the admin meant — a plan with no
  // pages ticked unlocks nothing, a renamed page is never granted, a deleted
  // master feature never renders. The server reports those; a toast per line
  // is what turns them from a log entry into something anyone acts on.
  const showPlanWarnings = (warnings) => {
    (warnings || []).forEach((message) => {
      toast(message, { icon: '⚠️', duration: 9000 });
    });
  };

  const handleSavePlan = async () => {
    // Validate required fields
    if (!formData.name || !formData.name.trim()) {
      toast.error('Please enter a plan name');
      return;
    }

    // Cognitive Agents plans have no Price input to validate — the field is
    // read-only and derived from the Platform Fee, so `formData.price` stays
    // '' and this gate would reject every such plan. Validate the money the
    // admin actually typed instead.
    const isAgentPlan = formData.planCategory === AGENT_PLAN_CATEGORY;
    // Allow 0 for free plans
    const priceNum = isAgentPlan ? 0 : parseFloat(formData.price);
    if (isAgentPlan) {
      const platformFee = parseFloat(formData.fees.platformFee);
      if (formData.fees.platformFee === '' || isNaN(platformFee) || platformFee < 0) {
        toast.error('Please enter a valid Platform Fee (must be >= 0)');
        return;
      }
      // Required, not merely defaulted: a plan with 0 included runs blocks
      // every run the subscriber starts, so leaving this box empty takes the
      // customer offline the moment they subscribe.
      const includedRuns = parseInt(formData.fees.includedRuns, 10);
      if (formData.fees.includedRuns === '' || isNaN(includedRuns) || includedRuns < 1) {
        toast.error('Included Runs is required (must be at least 1) — a plan with 0 runs blocks every run');
        return;
      }
    } else if (formData.price === '' || isNaN(priceNum) || priceNum < 0) {
      toast.error('Please enter a valid price (must be >= 0)');
      return;
    }

    if (!formData.currency) {
      toast.error('Please select a currency');
      return;
    }

    if (!formData.interval) {
      toast.error('Please select a billing interval');
      return;
    }

    setSaving(true);
    try {
      const planData = {
        name: formData.name.trim(),
        description: formData.description || '',
        price: priceNum,
        currency: formData.currency,
        interval: formData.interval,
        features: formData.features || [],
        planCategory: formData.planCategory || '',
        // Sent only for Cognitive Agents plans; the server derives `price` from
        // this. `price: 0` clears any figure left over from another category,
        // which the server would otherwise reject as contradictory.
        ...(formData.planCategory === AGENT_PLAN_CATEGORY ? {
          price: 0,
          fees: {
            platformFeeMinorUnits: Math.round((Number(formData.fees.platformFee) || 0) * 100),
            includedRuns: Number(formData.fees.includedRuns) || 0,
            includedCredits: Number(formData.fees.includedCredits) || 0,
            grossMarginTarget: (Number(formData.fees.grossMarginPct) || 0) / 100,
            annualDiscountPct: (Number(formData.fees.annualDiscountPct) || 0) / 100,
            originalPriceMinorUnits: Math.round((Number(formData.fees.originalPrice) || 0) * 100),
            isPopular: !!formData.fees.isPopular
          }
        } : {}),
        razorpay_plan_id: formData.razorpay_plan_id || null,
        is_active: formData.is_active !== undefined ? formData.is_active : true,
        visible_to_shops: formData.visible_to_shops || [],
        masterFeatures: formData.masterFeatures || [],
        isDefaultPlan: formData.isDefaultPlan === true
      };

      console.log('Sending plan data:', planData); // Debug log

      if (editingPlan) {
        // Update plan
        const response = await axios.put(`${app_url}/api/subscriptions/plans/${editingPlan._id}`, planData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.data.success) {
          toast.success('Plan updated successfully');
          showPlanWarnings(response.data.warnings);
          setShowCreateModal(false);
          fetchPlans();
        } else {
          toast.error(response.data.message || 'Failed to update plan');
        }
      } else {
        const response = await axios.post(`${app_url}/api/subscriptions/plans/create`, planData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.data.success) {
          toast.success('Plan created successfully');
          showPlanWarnings(response.data.warnings);
          setShowCreateModal(false);
          fetchPlans();
        } else {
          toast.error(response.data.message || 'Failed to create plan');
        }
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save plan';
      toast.error(errorMessage);
      console.error('Full error response:', error.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${amount}`;
  };

  if (user_role !== 'admin') {
    return (
      <div className="main-container">
        <div className="admin-header">
          <h1 className="admin-title">Access Denied</h1>
          <p className="admin-subtitle">Admin privileges required to access this page</p>
        </div>
        <Container fluid className="admin-content">
          <div className="admin-section">
            <Alert variant="danger" className="mb-0">You don't have permission to access this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="main-container">
        <div className="admin-loading-inline">
          <div className="admin-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading pricing plans...</p>
          </div>
        </div>
      </div>
    );
  }

  const getVisiblePages = () => {
    const q = (featureSearch || '').trim().toLowerCase();
    return availablePages.filter(p => !q || p.toLowerCase().includes(q));
  };

  const activePlans = plans.filter(plan => plan.is_active !== false);
  const inactivePlans = plans.filter(plan => plan.is_active === false);

  return (
    <div className={`${embedded ? '' : 'main-container '}pricing-plan-management-container`}>
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <h1 className="admin-section-title">Pricing Plan Management</h1>
            <p className="admin-subtitle">Create and manage subscription pricing plans</p>
          </div>
          <div className="admin-header-right d-flex gap-2 align-items-center">
            <Button
              variant="primary"
              onClick={() => setShowMasterFeaturesModal(true)}
              className="admin-btn"
              style={{ color: 'white', fontWeight: 500 }}
            >
              ⚙️ Manage Master Features
            </Button>
            {embedded ? (
              <Button
                variant="primary"
                onClick={() => navigate('/admin/subscriptions')}
                className="admin-btn"
              >
                Plan Analytics
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/admin')}
                className="admin-btn"
              >
                ← Back to Dashboard
              </Button>
            )}
            <Button variant="primary" onClick={handleCreatePlan} className="admin-btn">
              + Create New Plan
            </Button>
          </div>
        </div>
      </div>

      <Container fluid className="admin-content">

        {plans.length === 0 && (
          <Row>
            <Col>
              <div className="admin-section">
                <div className="text-center py-5">
                  <p className="admin-subtitle">No pricing plans created yet. Create your first plan to get started.</p>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {activePlans.length > 0 && (
          <div className="admin-section mb-4">
            <div className="admin-section-header mb-3">
              <h2 className="admin-section-title mb-0">Active Plans</h2>
              <p className="admin-section-description mb-0">
                These plans are currently active and available for use.
              </p>
            </div>
            <Row>
              {activePlans.map((plan) => (
                <Col key={plan._id} xs={12} md={6} lg={4} className="mb-4">
                  <div className={`admin-section plan-card pricing-plan-card`}>
                    <div className="admin-section-header">
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <h3 className="admin-section-title mb-0">{plan.name}</h3>
                        <div className="d-flex gap-1 align-items-center flex-wrap">
                          {plan.planCategory && (
                            <Badge bg="info" style={{ textTransform: 'capitalize', fontSize: '11px' }}>
                              {plan.planCategory.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {/* The plan new signups actually land on, as the
                              server resolves it — which is not necessarily the
                              one someone ticked. Showing the resolved answer is
                              the point: the bug this replaced was an unflagged
                              catalogue silently picking the oldest free row. */}
                          {normalizeId(plan._id) === defaultPlanId && (
                            <Badge bg="primary">Default for signups</Badge>
                          )}
                          <Badge bg="success">Active</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="plan-card-content">
                      <div className="plan-price-section mb-3">
                        <div className="plan-price-display">
                          <span className="plan-price-amount">{formatCurrency(plan.price, plan.currency)}</span>
                          <span className="plan-price-interval">/{plan.interval}</span>
                        </div>
                        {plan.description && (
                          <p className="admin-section-description mt-2 mb-0">{plan.description}</p>
                        )}
                      </div>
                      {/* Hide features(Page visibility) for now since they are not being used currently and just add clutter - we can always add them back later if needed */}
                      {/* {plan.features && plan.features.length > 0 && (
                        <div className="plan-features-section mb-3">
                          <h6 className="plan-features-title">Features:</h6>
                          <ul className="plan-features-list">
                            {plan.features.map((feature, index) => (
                              <li key={index}>
                                <span className="feature-check">✓</span>
                                <span className="feature-text">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )} */}
                      {plan.masterFeatures && plan.masterFeatures.length > 0 && (
                        <div className="mb-3">
                          {/* plan-features-section */}
                          <h6 className="plan-features-title">Features:</h6>
                          <ul className="plan-features-list">
                            {plan.masterFeatures.map((featureId, index) => {
                              const feature = masterFeatures.find(f => normalizeId(f._id) === normalizeId(featureId));
                              return feature ? (
                                <li key={index}>
                                  <span className="feature-check">✓</span>
                                  <span className="feature-text">{feature.name}</span>
                                </li>
                              ) : null;
                            })}
                          </ul>
                        </div>
                      )}

                      <div className="plan-meta-info mb-3">
                        {plan.razorpay_plan_id && (
                          <div className="plan-meta-item">
                            <span className="plan-meta-label">Razorpay Plan ID:</span>
                            <code className="plan-meta-value">{plan.razorpay_plan_id}</code>
                          </div>
                        )}
                        <div className="plan-meta-item">
                          <span className="plan-meta-label">Visibility:</span>
                          <span className="plan-meta-value">
                            {!plan.visible_to_shops || plan.visible_to_shops.length === 0
                              ? 'All Shops'
                              : `${plan.visible_to_shops.length} Shop(s)`}
                          </span>
                        </div>
                      </div>

                      <div className="plan-actions d-grid gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditPlan(plan)}
                          className="w-100"
                        >
                          Edit Plan
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleManageVisibility(plan)}
                          className="w-100"
                        >
                          Manage Visibility
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {inactivePlans.length > 0 && (
          <div className="admin-section">
            <div className="admin-section-header mb-3">
              <h2 className="admin-section-title mb-0">Inactive Plans</h2>
              <p className="admin-section-description mb-0">
                These plans are inactive but kept for reference or future use. You can still edit them.
              </p>
            </div>
            <Row>
              {inactivePlans.map((plan) => (
                <Col key={plan._id} xs={12} md={6} lg={4} className="mb-4">
                  <div className={`admin-section plan-card pricing-plan-card inactive-plan`}>
                    <div className="admin-section-header">
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <h3 className="admin-section-title mb-0">{plan.name}</h3>
                        <Badge bg="secondary">Inactive</Badge>
                      </div>
                    </div>

                    <div className="plan-card-content">
                      <div className="plan-price-section mb-3">
                        <div className="plan-price-display">
                          <span className="plan-price-amount">{formatCurrency(plan.price, plan.currency)}</span>
                          <span className="plan-price-interval">/{plan.interval}</span>
                        </div>
                        {plan.description && (
                          <p className="admin-section-description mt-2 mb-0">{plan.description}</p>
                        )}
                      </div>

                      {plan.features && plan.features.length > 0 && (
                        <div className="plan-features-section mb-3">
                          <h6 className="plan-features-title">Features:</h6>
                          <ul className="plan-features-list">
                            {plan.features.map((feature, index) => (
                              <li key={index}>
                                <span className="feature-check">✓</span>
                                <span className="feature-text">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {plan.masterFeatures && plan.masterFeatures.length > 0 && (
                        <div className="plan-features-section mb-3">
                          <h6 className="plan-features-title">Master Features:</h6>
                          <ul className="plan-features-list">
                            {plan.masterFeatures.map((featureId, index) => {
                              const feature = masterFeatures.find(f => normalizeId(f._id) === normalizeId(featureId));
                              return feature ? (
                                <li key={index}>
                                  <span className="feature-check">✓</span>
                                  <span className="feature-text">{feature.name}</span>
                                </li>
                              ) : null;
                            })}
                          </ul>
                        </div>
                      )}

                      <div className="plan-meta-info mb-3">
                        {plan.razorpay_plan_id && (
                          <div className="plan-meta-item">
                            <span className="plan-meta-label">Razorpay Plan ID:</span>
                            <code className="plan-meta-value">{plan.razorpay_plan_id}</code>
                          </div>
                        )}
                        <div className="plan-meta-item">
                          <span className="plan-meta-label">Visibility:</span>
                          <span className="plan-meta-value">
                            {!plan.visible_to_shops || plan.visible_to_shops.length === 0
                              ? 'All Shops'
                              : `${plan.visible_to_shops.length} Shop(s)`}
                          </span>
                        </div>
                      </div>

                      <div className="plan-actions d-grid gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditPlan(plan)}
                          className="w-100"
                        >
                          Edit Plan
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleManageVisibility(plan)}
                          className="w-100"
                        >
                          Manage Visibility
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {plans.length === 0 && (
          <Alert variant="info" className="text-center">
            No pricing plans found. Create your first plan to get started.
          </Alert>
        )}

        {/* Create/Edit Plan Modal */}
        <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); setVisibilityShopSearch(''); }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Plan Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Basic Plan"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    {/* Cognitive Agents plans DERIVE their price from the fee
                        block below, so offering an editable Price here as well
                        meant two money inputs with only one of them wired —
                        a plan saved with 36 typed here stored 0 and became
                        free (test_plan_cux_agents1, 2026-08-19). One input. */}
                    <Form.Label>
                      {formData.planCategory === AGENT_PLAN_CATEGORY ? 'Price (derived)' : 'Price *'}
                    </Form.Label>
                    {formData.planCategory === AGENT_PLAN_CATEGORY ? (
                      <>
                        <Form.Control
                          type="text"
                          readOnly
                          disabled
                          value={`${formData.currency === 'USD' ? '$' : '₹'}${agentPlanCharge(formData.fees, formData.interval).toLocaleString()} / ${formData.interval === 'yearly' ? 'year' : 'month'}`}
                        />
                        <Form.Text className="text-muted">
                          Set the Platform Fee under “Cognitive Agents fees” below.
                        </Form.Text>
                      </>
                    ) : (
                      <Form.Control
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="999"
                        step="0.01"
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Plan description"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Billing Interval</Form.Label>
                    <Form.Select
                      value={formData.interval}
                      onChange={(e) => setFormData({ ...formData, interval: e.target.value })}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Razorpay Plan ID (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.razorpay_plan_id}
                  onChange={(e) => setFormData({ ...formData, razorpay_plan_id: e.target.value })}
                  placeholder="plan_xxxxxxxxxxxxx"
                />
                <Form.Text className="text-muted">
                  If you've created a plan in Razorpay dashboard, enter its ID here
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Plan Category</Form.Label>
                <Form.Select
                  value={formData.planCategory || ''}
                  onChange={(e) => {
                    const cat = e.target.value;
                    let preSelected = null;
                    if (cat === 'keyword_personalization') preSelected = KEYWORD_PERSONALIZATION_PAGES;
                    else if (cat === 'layout_personalization') preSelected = LAYOUT_PERSONALIZATION_PAGES;
                    else if (cat === 'general') preSelected = availablePages;
                    // `null` means this category has no preset — Cognitive Agents
                    // and the blank option. It must NOT be read as "preset of
                    // nothing": the old code assigned `[]` in that case, so
                    // picking Cognitive Agents (or clearing the category) wiped
                    // every page the admin had already ticked. A plan saved that
                    // way carries `features: []`, syncFeaturesToShop returns
                    // early on an empty list, and the subscriber is granted no
                    // pages at all — the plan looks fine in the admin list and
                    // unlocks nothing. Keep what is there unless a preset says
                    // otherwise.
                    setFormData({
                      ...formData,
                      planCategory: cat,
                      features: preSelected
                        ? preSelected.filter(p => availablePages.includes(p))
                        : formData.features,
                    });
                    setFeatureSearch('');
                  }}
                >
                  <option value="">Select a category (optional)</option>
                  <option value="keyword_personalization">Keyword Personalization</option>
                  <option value="layout_personalization">Layout Personalization</option>
                  <option value="general">General (All Features)</option>
                  <option value="cognitive_agents">Cognitive Agents</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Selecting a category pre-checks the relevant features below. You can still customise individually.
                </Form.Text>
              </Form.Group>

              {formData.planCategory === AGENT_PLAN_CATEGORY && (
                <div className="mb-3 p-3 border rounded bg-light">
                  <div className="fw-semibold mb-1">Cognitive Agents fees</div>
                  <Form.Text className="text-muted d-block mb-3">
                    All figures are monthly. The annual price is derived from them, so the
                    Price field above is ignored for this category.
                  </Form.Text>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Platform Fee (per month)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.01"
                          value={formData.fees.platformFee}
                          onChange={(e) => {
                            // Typing the fee is what arms the auto-fill below.
                            setPlatformFeeTouched(true);
                            setFormData({ ...formData, fees: { ...formData.fees, platformFee: e.target.value } });
                          }}
                          placeholder="5000"
                        />
                        <Form.Text className="text-muted">
                          Included Runs is derived from this — see the run calculator below.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Included Runs (per month) *</Form.Label>
                        <Form.Control
                          type="number" min="1" step="1"
                          value={formData.fees.includedRuns}
                          onChange={(e) => {
                            // A typed figure is a deliberate override, so stop
                            // the calculator overwriting it on the next
                            // keystroke. The checkbox hands control back.
                            setAutoRunsFromFee(false);
                            setFormData({ ...formData, fees: { ...formData.fees, includedRuns: e.target.value } });
                          }}
                          placeholder="200"
                        />
                        <Form.Text className="text-muted">
                          {autoRunsFromFee && platformFeeTouched
                            ? 'Auto-filled from the Platform Fee. Type here to override.'
                            : 'Unused runs expire at month end. Required — a plan with 0 runs blocks every run.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Included AI Credits (per month)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="1"
                          value={formData.fees.includedCredits}
                          onChange={(e) => setFormData({ ...formData, fees: { ...formData.fees, includedCredits: e.target.value } })}
                          placeholder="10000"
                        />
                        <Form.Text className="text-muted">1 credit = ₹1. Also expire monthly.</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Gross Margin (%)</Form.Label>
                        <Form.Control
                          type="number" min="0" max="99" step="1"
                          value={formData.fees.grossMarginPct}
                          onChange={(e) => setFormData({ ...formData, fees: { ...formData.fees, grossMarginPct: e.target.value } })}
                          placeholder="50"
                        />
                        <Form.Text className="text-muted">Applied to vendor cost when pricing usage.</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Annual Discount (%)</Form.Label>
                        <Form.Control
                          type="number" min="0" max="99" step="1"
                          value={formData.fees.annualDiscountPct}
                          onChange={(e) => setFormData({ ...formData, fees: { ...formData.fees, annualDiscountPct: e.target.value } })}
                          placeholder="20"
                        />
                        <Form.Text className="text-muted">Only applies when billed yearly.</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Original Price (per month)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.01"
                          value={formData.fees.originalPrice}
                          onChange={(e) => setFormData({ ...formData, fees: { ...formData.fees, originalPrice: e.target.value } })}
                          placeholder="Leave blank for no discount"
                        />
                        <Form.Text className="text-muted">
                          Shown crossed out next to the price, with a “Limited Time Offer” tag.
                          Must be higher than the Platform Fee. Never charged.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Highlight</Form.Label>
                        <Form.Check
                          type="checkbox"
                          id="agent-plan-popular"
                          label="Show a “Most Popular” badge"
                          checked={!!formData.fees.isPopular}
                          onChange={(e) => setFormData({ ...formData, fees: { ...formData.fees, isPopular: e.target.checked } })}
                        />
                        <Form.Text className="text-muted">
                          The badge is hidden on a plan the shop is already on.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Charge per {formData.interval === 'yearly' ? 'year' : 'month'}</Form.Label>
                        <Form.Control
                          plaintext readOnly
                          className="fw-semibold fs-5"
                          value={`${formData.currency === 'USD' ? '$' : '₹'}${agentPlanCharge(formData.fees, formData.interval).toLocaleString()}`}
                        />
                        <Form.Text className="text-muted">
                          {formData.interval === 'yearly'
                            ? 'monthly x 12, less the annual discount'
                            : 'the platform fee as entered'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <PlanRunCalculator
                    appUrl={app_url}
                    fees={formData.fees}
                    currency={formData.currency}
                    autoApply={autoRunsFromFee && platformFeeTouched}
                    onAutoChange={(on) => {
                      setAutoRunsFromFee(on);
                      // Ticking the box is itself an instruction to fill the
                      // field now, not merely on the next fee keystroke.
                      if (on) setPlatformFeeTouched(true);
                    }}
                    onApplyRuns={applyDerivedRuns}
                  />
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Manage Features/Page visibility <span className="text-muted fw-normal">({formData.features.length} selected)</span></Form.Label>
                {pagesLoading ? (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Spinner animation="border" size="sm" /> Loading features...
                  </div>
                ) : (
                  <>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Search features..."
                        value={featureSearch}
                        onChange={(e) => setFeatureSearch(e.target.value)}
                      />
                      <Button variant="outline-secondary" size="sm"
                        onClick={() => setFormData({ ...formData, features: [...new Set([...formData.features, ...getVisiblePages()])] })}>
                        All
                      </Button>
                      <Button variant="outline-secondary" size="sm"
                        onClick={() => setFormData({ ...formData, features: formData.features.filter(f => !getVisiblePages().includes(f)) })}>
                        None
                      </Button>
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '6px', padding: '10px' }}>
                      {getVisiblePages().length === 0 && (
                        <p className="text-muted small mb-0">No features match your search.</p>
                      )}
                      {getVisiblePages().map((page) => (
                        <Form.Check
                          key={page}
                          type="checkbox"
                          label={page}
                          checked={formData.features.includes(page)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.features, page]
                              : formData.features.filter(f => f !== page);
                            setFormData({ ...formData, features: updated });
                          }}
                          className="mb-1"
                        />
                      ))}
                    </div>
                  </>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Feature/Master Features <span className="text-muted fw-normal">({formData.masterFeatures?.length || 0} selected)</span></Form.Label>
                {masterFeaturesLoading ? (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Spinner animation="border" size="sm" /> Loading master features...
                  </div>
                ) : masterFeatures.length === 0 ? (
                  <p className="text-muted small mb-0">No master features available. Create master features from the "Manage Master Features" button.</p>
                ) : (
                  <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '6px', padding: '10px' }}>
                    {masterFeatures.filter(f => f.is_active).map((feature) => (
                      <Form.Check
                        key={feature._id}
                        type="checkbox"
                        label={feature.name}
                        checked={(formData.masterFeatures || []).some(id => normalizeId(id) === normalizeId(feature._id))}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...(formData.masterFeatures || []), feature._id]
                            : (formData.masterFeatures || []).filter(f => f !== feature._id);
                          setFormData({ ...formData, masterFeatures: updated });
                        }}
                        className="mb-1"
                      />
                    ))}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Plan is active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="plan-is-default"
                  label="Default plan for new signups"
                  checked={formData.isDefaultPlan === true}
                  onChange={(e) => setFormData({ ...formData, isDefaultPlan: e.target.checked })}
                />
                <Form.Text className="text-muted">
                  New accounts are provisioned onto this plan automatically, with no payment
                  step, and it is what "skip" leaves them on during setup. Only one plan can
                  hold this — ticking it here clears it everywhere else. Leave it unticked and
                  the free Cognitive Agents plan is used.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Visible to Shops</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  Leave empty to make plan visible to all shops. Select specific shops to restrict visibility.
                </Form.Text>
                {shopsLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Form.Control
                      type="text"
                      placeholder="Search shops by name or store..."
                      value={visibilityShopSearch}
                      onChange={(e) => setVisibilityShopSearch(e.target.value)}
                      className="mb-2 visibility-shop-search"
                    />
                    <div className="shops-checkbox-list">
                      {getFilteredShopsForVisibility(shops).map((shop) => (
                        <Form.Check
                          key={shop.shop}
                          type="checkbox"
                          label={`${shop.shop_name || shop.shop} (${shop.shop})`}
                          checked={formData.visible_to_shops?.includes(shop.shop)}
                          onChange={() => handleToggleShopVisibility(shop.shop)}
                          className="mb-2"
                        />
                      ))}
                      {getFilteredShopsForVisibility(shops).length === 0 && (
                        <p className="text-muted small mb-0">No shops match your search.</p>
                      )}
                    </div>
                  </>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleSavePlan} disabled={saving} className="admin-btn">
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                editingPlan ? 'Update Plan' : 'Create Plan'
              )}
            </Button>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="admin-btn">
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Plan Visibility Management Modal */}
        <Modal show={showVisibilityModal} onHide={() => { setShowVisibilityModal(false); setVisibilityShopSearch(''); }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Manage Plan Visibility: {selectedPlanForVisibility?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Select which shops can see and subscribe to this plan. Leave all unchecked to make it visible to all shops.
            </p>
            {shopsLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Search shops by name or store..."
                  value={visibilityShopSearch}
                  onChange={(e) => setVisibilityShopSearch(e.target.value)}
                  className="mb-3 visibility-shop-search"
                />
                <div className="shops-checkbox-list shops-checkbox-list-modal">
                  {getFilteredShopsForVisibility(shops).map((shop) => (
                    <Form.Check
                      key={shop.shop}
                      type="checkbox"
                      label={
                        <div>
                          <strong>{shop.shop_name || shop.shop}</strong>
                          <br />
                          <small className="text-muted">{shop.shop}</small>
                        </div>
                      }
                      checked={formData.visible_to_shops?.includes(shop.shop)}
                      onChange={() => handleToggleShopVisibility(shop.shop)}
                      className="mb-3"
                    />
                  ))}
                  {getFilteredShopsForVisibility(shops).length === 0 && (
                    <p className="text-muted text-center mb-0">No shops match your search.</p>
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleSaveVisibility} disabled={saving} className="admin-btn">
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Visibility'
              )}
            </Button>
            <Button variant="secondary" onClick={() => setShowVisibilityModal(false)} className="admin-btn">
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Master Features Management Modal */}
        <Modal
          show={showMasterFeaturesModal}
          onHide={() => setShowMasterFeaturesModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Manage Master Features</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MasterFeatureListManagement
              app_url={app_url}
              embedded={true}
              onClose={() => {
                setShowMasterFeaturesModal(false);
                fetchMasterFeatures(); // Refresh master features after management
              }}
            />
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default PricingPlanManagement;
